<template>
  <div
    :class="[
      'relative mx-auto overflow-hidden px-0 md:box-content',
      'md:max-w-[1184px] md:pt-6',
      'max-h-dvh md:px-7'
    ]"
  >
    <button
      v-if="isMdAndUp"
      type="button"
      class="!absolute right-0 top-2 size-9 w-fit"
      @click="emit('close')"
    >
      <UiIcon name="x-mark" class="text-icon-main size-5 md:size-6" />
    </button>
    <div
      ref="resultsBlock"
      class="scrollbar-hidden container-block relative h-full overflow-hidden overflow-y-scroll bg-primary md:h-[76px] md:rounded-4xl"
      @scroll="onResultsScroll"
    >
      <header
        class="fixed top-0 z-40 w-full rounded-b-3xl bg-primary pt-3 md:sticky md:overflow-visible md:rounded-none md:border-0 md:pt-0 md:shadow-none"
      >
        <UiContainer
          class="relative z-20 flex items-center overflow-hidden bg-primary pb-3 transition-all duration-500 md:bg-primary md:pt-6"
        >
          <UiButton
            v-if="isMdAndDown"
            severity="quaternary"
            class="flex !size-9 items-center justify-center"
            @click="emit('close')"
          >
            <UiIcon name="chevron-down" class="size-4 rotate-90 text-icon-primary" />
          </UiButton>
          <form class="input w-full" @submit.prevent>
            <UiInput
              ref="input"
              :model-value="searchQuery"
              severity="primary"
              type="search"
              maxlength="255"
              class="!rounded-full md:!pl-[184px]"
              placeholder="найти"
              @update:model-value="onTextInput"
            >
              <template v-if="isMdAndUp" #prepend>
                <div class="absolute left-3 top-[7px] md:w-[170px]">
                  <SearchModeSelect v-model="mode" @update:model-value="scrollToTop" />
                </div>
              </template>
            </UiInput>
          </form>
          <UiButton
            v-if="isMdAndDown && smallHeader"
            severity="quaternary"
            class="flex !size-9 items-center justify-center"
            @click="toggleHideHeader(false)"
          >
            <UiIcon name="three-dots" class="size-6" />
            <div
              v-if="selectedFiltersCount"
              class="absolute -right-0.5 -top-0.5 z-20 flex size-4 items-center justify-center rounded-full text-xxs text-light bg-main"
            >
              {{ selectedFiltersCount }}
            </div>
          </UiButton>
        </UiContainer>
        <div class="flex w-full items-center pb-3" :class="{ '!pb-0': smallHeader }">
          <FilterSearch
            :id="SearchFilterIds.searchModalSearchFilter"
            :hide-filters="!isSearchForEvents"
            list-classes="!pl-0"
            :class="{ '!absolute -translate-y-full opacity-0': smallHeader }"
            :page-size="PAGE_SIZE"
            class="content bg-primary !px-4 !pr-0 transition-all duration-500 md:w-full md:translate-y-[-50vh]"
            :default-select="SearchSorting.nearest"
            :search-mode="isMdAndDown ? mode : undefined"
            @update:search-mode="
              ($event) => {
                mode = $event
                scrollToTop()
              }
            "
            @change="onFilterChange"
          />
        </div>
      </header>
      <UiSearchError
        v-if="showSearchError"
        class="absolute inset-0 flex h-full flex-col justify-center text-center"
        @reload="onSearch"
      />
      <EmptySearch
        v-else-if="searchResultFiltered && !searchResultFiltered.length"
        class="absolute inset-0 my-auto flex size-full items-center justify-center !py-0"
      />
      <template v-if="loading">
        <UiLoader size="60" class="!fixed inset-0 z-30 size-full" />
        <SearchResultsCategorySkeleton v-for="skeleton in 5" :key="skeleton" class="mb-9" />
      </template>
      <section
        class="content z-10 mt-[118px] pb-16 pt-7 md:!mt-0 md:translate-y-[-50vh] md:pb-0 md:pt-6"
      >
        <template v-if="!loading">
          <template v-if="!showSearchError">
            <SearchResults
              v-if="searchResultFiltered"
              :value="searchResultFiltered"
              class="md:pb-9"
            />
            <UiEternalLoading
              v-if="(selectedFiltersCount || searchQuery) && !allItemsLoaded"
              :handler="onEndScroll"
              :all-loaded="allItemsLoaded"
            />
          </template>
        </template>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import isEqual from 'lodash/isEqual'
import throttle from 'lodash/throttle.js'
import { storeToRefs } from 'pinia'
import {
  computed,
  defineAsyncComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  type ShallowRef,
  useTemplateRef
} from 'vue'
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router'
import { FilterSearch } from '@/4_features/Search'
import {
  SearchFilterIds,
  type SearchMode,
  SearchModeSelect,
  useSearch,
  useSearchFilters
} from '@/5_entities/Search'
import { AbortError } from '@/6_shared/api'
import { useHiddenHeader } from '@/6_shared/composables'
import { useBreakpoint } from '@/6_shared/lib'
import { type BaseSearchFilters, SearchSorting } from '@/6_shared/model'
import {
  UiButton,
  UiContainer,
  UiEternalLoading,
  UiIcon,
  UiInput,
  UiLoader,
  UiSearchError
} from '@/6_shared/ui'
import EmptySearch from '../Search/EmptySearch.vue'

type EmitType = {
  (e: 'close'): void
}
const emit = defineEmits<EmitType>()

const SearchResults = defineAsyncComponent(() => import('./SearchResults.vue'))
const SearchResultsCategorySkeleton = defineAsyncComponent(
  () => import('./Skeleton/SearchResultsCategorySkeleton.vue')
)
//vue helper
const {
  md: [isMdAndUp, isMdAndDown]
} = useBreakpoint()
// stores
const { search, resetSearch, loadNexPage, setSearchIsFailed } = useSearch()
const { results, isAllVenuesLoaded, isAllEventsLoaded, searchErrors } = storeToRefs(useSearch())
const searchResultFiltered = computed(() => {
  if (!results.value) return

  return results.value[mode.value.value]
})
const allItemsLoaded = computed(() =>
  isSearchForEvents.value ? isAllEventsLoaded.value : isAllVenuesLoaded.value
)
//constants
const PAGE_SIZE = 15

//refs
const searchQuery = ref('')
const filters = ref<BaseSearchFilters>({
  pagination: { pageSize: PAGE_SIZE, currentPage: 1 },
  filter: {}
})
const loading = ref(false)
const showSearchError = computed(() => searchErrors.value[mode.value.value])

// main methods
/**
 * оборачивает поисковые запросы в обработчик ошибок
 * @param handler
 * @param needLoading
 */
const searchWrapper = async (handler: () => Promise<void>, needLoading: boolean = true) => {
  try {
    needLoading && (loading.value = true)
    needLoading && scrollToTop()

    await handler()
  } catch (e) {
    if (e instanceof AbortError) return
    setSearchIsFailed()
    console.error('failed to search ', e)
  } finally {
    loading.value = false
  }
}
/**
 * запускает поиск
 */
const onSearch = async () => {
  searchWrapper(async () => {
    filters.value.pagination.currentPage = 1

    await search(filters.value)
  })
}
/**
 * скроллит поиск наверх
 */
const scrollToTop = () => {
  if (!resultsBlock.value) return

  resultsBlock.value.scrollTop = 0
}
/**
 * срабатывает, когда доскроллили до конца выдачи
 */
const onEndScroll = async () => {
  await loadNexPage(filters.value)
  filters.value.pagination.currentPage++
}

//filters
const { onTextInput, onFilterChange, selectedFiltersCount } = useSearchFilters(
  searchQuery,
  filters,
  onSearch
)
const mode = ref<SearchMode>({
  title: 'по мероприятиям',
  value: 'events'
})
const isSearchForEvents = computed(() => mode.value.value === 'events')

// animation
const input = useTemplateRef<typeof UiInput>('input')
const focusOnInput = (input: Readonly<ShallowRef<typeof UiInput | null>>) => {
  input.value?.inputElement.focus()
}
const scrollContainerToTop = () => {
  resultsBlock.value!.scrollTop = 0
}
onMounted(() => {
  setTimeout(() => {
    scrollContainerToTop()
    focusOnInput(input)
  }, 100)
})

//mobile
const resultsBlock = useTemplateRef<HTMLElement>('resultsBlock')
/**
 * обрабатывает скролл блока для переключения щапки
 */
const MIN_SCROLL = 300
const onResultsScroll = throttle(function () {
  if (!resultsBlock.value || isMdAndUp.value) return

  const { scrollTop } = resultsBlock.value

  toggleHideHeader(scrollTop >= MIN_SCROLL)
}, 300)
const { smallHeader, toggleHideHeader } = useHiddenHeader(onResultsScroll)

onBeforeUnmount(() => {
  resetSearch()
})

onBeforeRouteLeave(({ name: fromName }, { name: toName }) => {
  if (fromName !== toName) emit('close')
})
onBeforeRouteUpdate(({ params: toParams }, { params: fromParams }) => {
  const newParam = !isEqual(toParams, fromParams)

  if (newParam) emit('close')
})
</script>

<style scoped>
@media screen and (min-width: 768px) {
  .input {
    animation: input 0.2s forwards ease-out;
    will-change: transform;
  }

  .content {
    will-change: transform;
    animation: content 0.25s forwards 0.3s ease-out;
  }

  .container-block {
    will-change: transform;
    animation: container 0.2s forwards 0.35s ease-out;
  }
}

@keyframes input {
  to {
    width: 100%;
    transform: none;
  }
}

@keyframes content {
  from {
    transform: translateY(-50vh);
  }

  to {
    transform: none;
  }
}

@keyframes container {
  from {
    height: 76px;
  }

  to {
    height: 100%;
  }
}
</style>
